/**
 * Domain model.
 *
 * Field names follow the mandatory catalogue in the BMF-Schreiben of
 * 11.03.2024, Abschnitt III (Mietwagenunternehmen). See
 * `01_Input/rechtliche-anforderungen.md` section 2. German terms are kept
 * where the legal term has no faithful English equivalent, because an auditor
 * reading the export needs to recognise them.
 *
 * Money is always integer cents. Distance is always integer metres. Neither is
 * ever a float: a logbook that rounds differently on two devices is a logbook
 * that fails reconciliation.
 */

/** ISO-8601 date, no time component: `2026-07-14`. */
export type IsoDate = string;

/** ISO-8601 instant in UTC: `2026-07-14T09:31:00.000Z`. */
export type IsoInstant = string;

/** `YYYY-MM`, used as the clear-text partition key on encrypted tables. */
export type MonthBucket = string;

export type Uuid = string;

/**
 * Which PBefG licence the operator holds. This drives the VAT rate and must
 * never be inferred from trip distance.
 *
 * - `mietwagen` — § 49 PBefG, chauffeur/limousine service. Always 19 %.
 * - `taxi` — § 47 PBefG. Eligible for the reduced 7 % rate on trips within one
 *   municipality or up to 50 km (§ 12 Abs. 2 Nr. 10 b UStG).
 */
export type LicenceType = 'mietwagen' | 'taxi';

/** Which value the logbook treats as authoritative for distance. */
export type DistanceSystemOfRecord = 'computed' | 'odometer';

export type PaymentMethod = 'bar' | 'ec' | 'kreditkarte' | 'rechnung' | 'app' | 'sonstiges';

/**
 * Language of the interface only.
 *
 * Everything the app generates — invoices, the GoBD archive, the
 * Verfahrensdokumentation — stays German whatever this says, because those
 * documents are addressed to German customers and to the Finanzamt.
 */
export type UiLanguage = 'de' | 'en';

/**
 * `Fahrttyp` per BMF 11.03.2024. The distance field is only mandatory for
 * `tariffahrt`, but we record it for every trip anyway.
 */
export type TripType = 'tariffahrt' | 'sonstige';

/**
 * How the booking reached the operator — § 49 Abs. 4 Satz 4 PBefG.
 *
 * The paper Auftrag-Eingangs-Buch (HUSS 19006, see
 * `01_Input/referenz/README.md`) offers four tick boxes: ☎, ✉, ⚡ and
 * `mündlich`. They map onto `telefon`, `email`, `funk` and `persoenlich`; the
 * remaining values are the electronic channels the printed form predates.
 */
export type OrderChannel =
  | 'telefon'
  | 'email'
  | 'funk'
  | 'app'
  | 'vermittler'
  | 'persoenlich'
  | 'stammkunde'
  | 'sonstiges';

/**
 * Where the operator took the job on — the `B | W | ⚡` columns of the paper
 * order book, and the reason that book exists.
 *
 * § 49 Abs. 4 Satz 2 PBefG only permits Mietwagen to run orders that came in at
 * the Betriebssitz or in the owner's home. Satz 3 adds the single exception of
 * an order received by phone or radio *during* a trip. An order accepted
 * anywhere else may not lawfully be driven, so this is a closed list rather
 * than free text.
 */
export type OrderAcceptancePlace = 'betriebssitz' | 'wohnung' | 'funk';

export type InvoiceStatus = 'entwurf' | 'offen' | 'bezahlt' | 'storniert' | 'ueberfaellig';

export type DunningLevel = 0 | 1 | 2 | 3;

export type EInvoiceFormat = 'facturx' | 'xrechnung-ubl' | 'xrechnung-cii' | 'keine';

// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------

export interface Driver {
  id: Uuid;
  username: string;
  displayName: string;
  /** `Fahrerkennung` per BMF 11.03.2024 — stable, human-readable, on exports. */
  driverCode: string;
  createdAt: IsoInstant;
  active: boolean;
}

export interface Vehicle {
  id: Uuid;
  /** `Mietwagenkennung` / Ordnungsnummer des Fahrzeugs. */
  vehicleCode: string;
  licensePlate: string;
  make: string;
  model: string;
  active: boolean;
  createdAt: IsoInstant;
}

export interface Passenger {
  id: Uuid;
  lastName: string;
  firstName: string;
  company?: string;
  phone?: string;
  email?: string;
  /** Billing address, when it differs from where they are picked up. */
  billingAddressId?: Uuid;
  vatId?: string;
  /** Corporate customers get invoiced; private ones usually pay per ride. */
  isBusiness: boolean;
  notes?: string;
  createdAt: IsoInstant;
  /** Maintained by the suggestion engine, not by the user. */
  useCount: number;
  lastUsedAt: IsoInstant;
}

export interface Address {
  id: Uuid;
  /** What the driver typed or picked; shown verbatim in suggestions. */
  label: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  country: string;
  lat?: number;
  lon?: number;
  /** Set when the coordinates came from the geocoder rather than by hand. */
  geocodedAt?: IsoInstant;
  createdAt: IsoInstant;
  useCount: number;
  lastUsedAt: IsoInstant;
}

/**
 * The booking-intake record required by § 49 Abs. 4 Satz 4 PBefG. Kept as its
 * own entity rather than a field on the trip, because the statute requires the
 * intake to be logged when it happens — which is before the trip exists.
 *
 * The field list follows the columns of the Auftrag-Eingangs-Buch (HUSS 19006),
 * transcribed in `01_Input/referenz/README.md`. That form separates *arrival*
 * of the booking from *acceptance* of the job, and so does this record: the two
 * are usually seconds apart but legally distinct, and only the second one
 * decides whether the trip may be driven at all.
 */
export interface Order {
  id: Uuid;
  /**
   * `Lfd. Nr.` — restarts at 1 each calendar year and never skips a value. The
   * paper book derives its completeness from an unbroken number sequence, and
   * the cover records which numbers a book covers; this is the same guarantee.
   */
  sequenceNumber: number;
  /** When the booking reached the operator. Not when the trip took place. */
  receivedAt: IsoInstant;
  channel: OrderChannel;

  /** `Besteller` — whoever placed the order, not necessarily the passenger. */
  ordererName?: string;
  ordererAddress?: string;
  ordererPhone?: string;
  ordererEmail?: string;

  /** `Abholstelle` (Straße/Haus-Nr.) — a Pflichtangabe on the paper form. */
  pickupLocation: string;
  /** `Zielort` (Ort/Straße), where it is known at intake. */
  destination?: string;
  /** The `evtl. Uhrzeit` column: when the customer wants to be collected. */
  requestedPickupAt?: IsoInstant;

  /**
   * `Beförderungsauftrag übernommen` — set once the job is taken on. Left empty
   * while the order is still only recorded, which the paper book cannot express
   * but a dispatcher logging a 7 a.m. call at 6 a.m. needs.
   */
  acceptedAt?: IsoInstant;
  acceptancePlace?: OrderAcceptancePlace;
  vehicleId?: Uuid;
  driverId?: Uuid;

  passengerId?: Uuid;
  /** `Bemerkung`. */
  notes?: string;
  /** Set once the trip is logged against this order. */
  tripId?: Uuid;
  createdAt: IsoInstant;
}

/**
 * One `Pause von – bis` row of the Mietwagen Fahrtennachweis, as local
 * `HH:MM` clock times. Stored as wall-clock rather than instants because that
 * is what the driver writes down and what an Arbeitszeit check reads back.
 */
export interface ShiftBreak {
  from: string;
  to: string;
}

/**
 * A working day for one driver in one vehicle. Carries the per-shift fields
 * from the BMF catalogue that do not belong on an individual trip, plus the
 * two the Mietwagen Fahrtennachweis (HUSS 18910) adds on top: breaks and the
 * driver's closing signature.
 */
export interface Shift {
  id: Uuid;
  driverId: Uuid;
  vehicleId: Uuid;
  date: IsoDate;
  /** `Arbeitsanfang Datum/Uhrzeit`. */
  startedAt: IsoInstant;
  /** `Arbeitsende Datum/Uhrzeit`. */
  endedAt?: IsoInstant;
  /** `Kilometerstand laut Tachometer bei arbeitstäglichem Fahrtbeginn`, metres. */
  odometerStartMeters: number;
  odometerEndMeters?: number;
  /**
   * `Pause von – bis`. Not a tax field, but § 4 ArbZG asks for it and it is
   * what explains a two-hour gap between trips before anyone assumes the gap
   * hides an unrecorded fare.
   */
  breaks?: ShiftBreak[];
  notes?: string;
  /** `Unterschrift Fahrer/in` — the name the driver confirmed the sheet under. */
  signedOffBy?: string;
  closedAt?: IsoInstant;
  createdAt: IsoInstant;
}

export interface Trip {
  id: Uuid;
  shiftId: Uuid;
  driverId: Uuid;
  vehicleId: Uuid;
  orderId?: Uuid;

  date: IsoDate;
  startedAt: IsoInstant;
  endedAt: IsoInstant;

  passengerId: Uuid;
  fromAddressId: Uuid;
  toAddressId: Uuid;

  tripType: TripType;

  /** Authoritative distance in metres — the value that goes on the invoice. */
  distanceMeters: number;
  /** What the routing API returned, kept even when overridden. */
  computedDistanceMeters?: number;
  /** True when the driver replaced the computed value by hand. */
  distanceOverridden: boolean;
  distanceSource: 'geoapify' | 'manual' | 'odometer';

  /** Gross fare in cents, including VAT. */
  fareGrossCents: number;
  vatRatePercent: number;
  paymentMethod: PaymentMethod;
  /** Only recorded when tax-relevant; § III of the BMF catalogue. */
  tipCents: number;

  /** The driver's free-text note. Distinct from the order record. */
  comment?: string;

  invoiceId?: Uuid;

  createdAt: IsoInstant;
  /**
   * Set 7 days after `date` per the safe harbour in BMF 03.03.2022 Rn. 29.
   * Once set, edits are still possible but only as a logged amendment.
   */
  lockedAt?: IsoInstant;
}

export interface InvoiceLine {
  id: Uuid;
  /** Present when the line came from a logged trip. */
  tripId?: Uuid;
  description: string;
  quantity: number;
  unit: string;
  unitPriceNetCents: number;
  vatRatePercent: number;
}

export interface Invoice {
  id: Uuid;
  /** Gapless within a numbering circle. Never reused, never reassigned. */
  invoiceNumber: string;
  /** The numbering circle this number was drawn from, e.g. `RE-2026`. */
  numberCircle: string;

  status: InvoiceStatus;
  issueDate: IsoDate;
  /** § 14 Abs. 4 Nr. 6 UStG — when the service was performed. */
  serviceDateFrom: IsoDate;
  serviceDateTo: IsoDate;
  dueDate: IsoDate;

  customerPassengerId?: Uuid;
  customerName: string;
  customerAddress: string;
  customerVatId?: string;
  /** BT-49 buyer electronic address. EN 16931 rule BR-63 makes it mandatory. */
  customerEmail?: string;
  /**
   * BT-10. For a public-sector recipient this is the Leitweg-ID, which
   * XRechnung rule BR-DE-15 requires; for a commercial one it is whatever
   * reference the buyer wants to see on the document.
   */
  buyerReference?: string;

  lines: InvoiceLine[];

  /** Cached totals in cents; recomputed on every save, never trusted blindly. */
  netTotalCents: number;
  vatTotalCents: number;
  grossTotalCents: number;

  /** § 33 UStDV short form applies — determined at issue time, then frozen. */
  isKleinbetrag: boolean;
  /** § 19 UStG — no VAT shown, exemption note required. */
  isKleinunternehmer: boolean;

  paymentTerms: string;
  notes?: string;

  paidAt?: IsoInstant;
  paidAmountCents?: number;

  dunningLevel: DunningLevel;
  lastDunnedAt?: IsoInstant;

  eInvoiceFormat: EInvoiceFormat;
  /** Set once an e-invoice has been generated, for the audit trail. */
  eInvoiceGeneratedAt?: IsoInstant;

  /** Points at the cancelled invoice when this is a Stornorechnung. */
  cancelsInvoiceId?: Uuid;

  createdAt: IsoInstant;
  issuedAt?: IsoInstant;
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export interface CompanySettings {
  companyName: string;
  ownerName: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  phone?: string;
  email?: string;
  website?: string;

  /** § 14 Abs. 4 Nr. 2 UStG — one of these two is mandatory on every invoice. */
  taxNumber?: string;
  vatId?: string;
  /**
   * Handelsregisternummer, e.g. `HRB 123456`. BT-30.
   *
   * Optional on a paper invoice, but EN 16931 rule BR-CO-26 wants the seller
   * identifiable by BT-29, BT-30 or BT-31, and a Kleinunternehmer has no
   * USt-IdNr. to satisfy BT-31 with. Without this an e-invoice from a
   * registered small business is rejected.
   */
  registrationNumber?: string;

  bankName?: string;
  iban?: string;
  bic?: string;

  licenceType: LicenceType;
  /** § 19 UStG small-business scheme: no VAT charged, exemption note required. */
  kleinunternehmer: boolean;

  logoDataUrl?: string;
  invoiceAccentColor: string;
  invoicePaymentTerms: string;
  invoiceFooterNote?: string;
}

export interface AppSettings {
  /**
   * Which distance value the logbook treats as authoritative.
   *
   * `computed` is the configured default per the project decision. It carries
   * an unresolved risk under § 1 Abs. 2 Satz 2 KassenSichV (in force since
   * 01.02.2026): an app that measures distance itself may be taken to assume
   * the function of a Wegstreckenzähler, which would pull it into the § 146a
   * AO regime with a certified TSE and an Eichrecht conformity assessment.
   * There is no case law on the provision. Switching to `odometer` moves the
   * system of record to the driver's manual reading without a data migration.
   *
   * See `01_Input/rechtliche-anforderungen.md` section 7.
   */
  distanceSystemOfRecord: DistanceSystemOfRecord;

  geoapifyApiKey?: string;
  /** Bias geocoding results towards the operating area. */
  geocodeBiasLat: number;
  geocodeBiasLon: number;
  geocodeCountryCode: string;

  /** Days after the trip date at which a record is frozen. */
  recordLockDays: number;
  /** Tolerance before the odometer reconciliation complains, in metres. */
  reconciliationToleranceMeters: number;

  backupWarnAfterDays: number;
  backupBlockAfterDays: number;
  lastBackupAt?: IsoInstant;

  defaultVehicleId?: Uuid;

  /**
   * Interface language. Kept here as well as in `localStorage` so that a
   * restored backup brings the choice with it; the pre-login screens have no
   * decrypted settings to read from and use the local copy.
   */
  language: UiLanguage;
}

export const DEFAULT_APP_SETTINGS: AppSettings = {
  distanceSystemOfRecord: 'computed',
  language: 'de',
  geocodeBiasLat: 48.1372,
  geocodeBiasLon: 11.5756,
  geocodeCountryCode: 'de',
  recordLockDays: 7,
  reconciliationToleranceMeters: 15_000,
  backupWarnAfterDays: 30,
  backupBlockAfterDays: 60,
};

export const DEFAULT_COMPANY_SETTINGS: CompanySettings = {
  companyName: '',
  ownerName: '',
  street: '',
  postalCode: '',
  city: '',
  country: 'DE',
  licenceType: 'mietwagen',
  kleinunternehmer: false,
  invoiceAccentColor: '#c19a5b',
  invoicePaymentTerms: 'Zahlbar innerhalb von 14 Tagen ohne Abzug.',
};

// ---------------------------------------------------------------------------
// Audit trail
// ---------------------------------------------------------------------------

export type AuditAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'lock'
  | 'unlock'
  | 'invoice.issue'
  | 'invoice.cancel'
  | 'invoice.pay'
  | 'invoice.dun'
  | 'shift.close'
  | 'export'
  | 'backup'
  | 'restore'
  | 'login'
  | 'account.create';

export type AuditEntityType =
  | 'trip'
  | 'shift'
  | 'order'
  | 'passenger'
  | 'address'
  | 'vehicle'
  | 'invoice'
  | 'settings'
  | 'driver'
  | 'system';

/** One field that changed, rendered inline wherever the record is shown. */
export interface FieldChange {
  field: string;
  /** German label, because this is read by the driver and by an auditor. */
  label: string;
  before: string | null;
  after: string | null;
}

/**
 * An append-only, hash-chained log entry.
 *
 * BFH VI B 37/23 (12.01.2024) requires the change history to be visible on
 * ordinary inspection of the file — a hidden table an administrator has to
 * query does not satisfy it. So these entries are rendered inline in the
 * record view and written into every export.
 */
export interface AuditEntry {
  /** Monotonic, gapless from 1. A gap is itself evidence of tampering. */
  seq: number;
  id: Uuid;
  at: IsoInstant;
  driverId: Uuid;
  action: AuditAction;
  entityType: AuditEntityType;
  entityId: Uuid;
  /** Human-readable summary, e.g. `Fahrt vom 14.07.2026, Kluse`. */
  entityLabel: string;
  changes: FieldChange[];
  /** Full snapshot before the change; the original is never overwritten. */
  before?: unknown;
  after?: unknown;
  reason?: string;
  /** Hash of the previous entry. Genesis uses 64 zeros. */
  prevHash: string;
  /** SHA-256 over the canonical serialisation of everything above. */
  hash: string;
}

export const GENESIS_HASH = '0'.repeat(64);
